import React, { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getAuthCookie } from "../../utils/authCookies";

const BatchPaymentReceipt: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken") || "";

  // Fetch PDF blob
  useEffect(() => {
    const fetchReportData = async () => {
      if (!reportId) {
        setError("No report ID provided");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        if (!token) throw new Error("No authentication token found");

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/batch-payments/transactions/${reportId}/receipt/download`,
          { headers: { Authorization: `Bearer ${token}`, Accept: "application/pdf" } }
        );
        if (!res.ok) throw new Error(`Failed to fetch report: ${res.statusText}`);

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
      } catch (e: any) {
        console.error(e);
        setError(e?.message || "Failed to fetch report data");
      } finally {
        setLoading(false);
      }
    };
    fetchReportData();
  }, [reportId, token]);

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  const handleBack = () => window.history.back();

  const handlePrint = () => {
    if (!pdfBlobUrl) return;
    
    // Create a hidden iframe to print the PDF
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    
    document.body.appendChild(iframe);
    
    iframe.src = pdfBlobUrl;
    
    iframe.onload = () => {
      setTimeout(() => {
        const iframeWindow = iframe.contentWindow;
        if (!iframeWindow) return;
        
        iframeWindow.focus();
        iframeWindow.print();
        
        // Listen for when user closes the print dialog
        // Clean up after print dialog is closed
        const cleanup = () => {
          try {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          } catch (e) {
            console.error("Error removing iframe:", e);
          }
        };
        
        // Remove iframe when focus returns to main window (print dialog closed)
        window.addEventListener('focus', cleanup, { once: true });
        
        // Fallback: remove after 30 seconds if focus event doesn't fire
        setTimeout(cleanup, 30000);
      }, 500);
    };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status" />
        <span className="ms-2">Loading report...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-3">
        <div className="alert alert-danger">
          <h4>Error Loading Report</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={handleBack}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!pdfBlobUrl) {
    return (
      <div className="container my-3">
        <div className="alert alert-warning">
          <h4>Report Not Found</h4>
          <p>No report data found. Please generate a new report.</p>
          <button className="btn btn-primary" onClick={handleBack}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid my-3">
      <style>{`
        @media print { .report-actions { display: none !important; } }
      `}</style>

      {/* Actions */}
      <div className="report-actions d-flex justify-content-between align-items-center mb-3">
        <button className="btn back-btn surface-btn fw-bold fs-16" onClick={handleBack}>
          <i className="bi bi-arrow-left me-1" />
          Back to Batch Payments
        </button>
        <div className="d-flex gap-2">
          <button className="btn back-btn surface-btn fw-bold fs-16" onClick={handlePrint}>
            <i className="bi bi-printer me-2" /> Print
          </button>
          <a
            className="btn back-btn surface-btn fw-bold fs-16"
            href={pdfBlobUrl}
            download={`Receipt-${reportId}.pdf`}
          >
            <i className="bi bi-download me-2" /> Get PDF
          </a>
        </div>
      </div>

      {/* PDF Preview - Simple embed */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <embed
            src={pdfBlobUrl}
            type="application/pdf"
            style={{
              width: '100%',
              height: 'calc(110vh - 200px)',
              border: 'none',
              borderRadius: '4px'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(BatchPaymentReceipt);
